import { Download, FileSpreadsheet, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface ExportOptionsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onExportReporting: () => void
  onExportForEditing: () => void
  isReportingExporting: boolean
  isEditingExporting: boolean
  canExportReporting: boolean
}

export function ExportOptionsModal({
  open,
  onOpenChange,
  onExportReporting,
  onExportForEditing,
  isReportingExporting,
  isEditingExporting,
  canExportReporting,
}: ExportOptionsModalProps) {
  const isExporting = isReportingExporting || isEditingExporting

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Export Assortment Plan</DialogTitle>
          <DialogDescription>
            Choose a format for the current plan version.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <section className="flex items-start justify-between gap-4 rounded-lg border p-4">
            <div className="flex gap-3">
              <FileSpreadsheet
                aria-hidden="true"
                className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
              />
              <div className="space-y-1">
                <h3 className="text-sm font-semibold">Export for Reporting</h3>
                <p className="text-sm text-muted-foreground">
                  Download the plan in the standard reporting format.
                </p>
                <p className="text-sm font-medium text-amber-800">
                  Rows without a SKU number will not be exported.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={onExportReporting}
              disabled={isExporting || !canExportReporting}
              aria-busy={isReportingExporting}
              aria-label="Export for Reporting"
            >
              {isReportingExporting ? (
                <Loader2 aria-hidden="true" className="animate-spin" />
              ) : (
                <Download aria-hidden="true" />
              )}
              {isReportingExporting ? 'Preparing…' : 'Export'}
            </Button>
          </section>

          <section className="flex items-start justify-between gap-4 rounded-lg border border-primary/40 bg-accent/20 p-4">
            <div className="flex gap-3">
              <FileSpreadsheet
                aria-hidden="true"
                className="mt-0.5 h-5 w-5 shrink-0 text-primary"
              />
              <div className="space-y-1">
                <h3 className="text-sm font-semibold">
                  Export for Editing (Import Format)
                </h3>
                <p className="text-sm text-muted-foreground">
                  Edit the workbook in Excel, then upload it using Import to
                  update matching SKUs.
                </p>
              </div>
            </div>
            <Button
              onClick={onExportForEditing}
              disabled={isExporting}
              aria-label="Export for Editing (Import Format)"
            >
              {isEditingExporting ? (
                <Loader2 aria-hidden="true" className="animate-spin" />
              ) : (
                <Download aria-hidden="true" />
              )}
              Export
            </Button>
          </section>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
